/**
 * Execution Store — SIMULATION ONLY
 *
 * Centralized state for trade preview + simulated execution.
 * ❌ NO real broker calls. ❌ NO Alpaca. ❌ NO account modification.
 */

import type {
  ExecutionState,
  ExecutionStatus,
  NormalizedPreview,
  NormalizedExecution,
  ExecutionErrorType,
  TradePreviewRequest,
} from '../types/execution';

import {
  previewTrade,
  executeSimulationTrade as adapterExecute,
} from '../services/executionAdapter';

// ═══════════════════════════════════════════════════════════════════════
//  INITIAL STATE
// ═══════════════════════════════════════════════════════════════════════

const INITIAL_STATE: ExecutionState = {
  status: 'idle',
  preview: null,
  execution: null,
  error: null,
  errorType: null,
};

// ═══════════════════════════════════════════════════════════════════════
//  STORE CLASS
// ═══════════════════════════════════════════════════════════════════════

type Listener = (state: ExecutionState) => void;

class ExecutionStoreClass {
  private state: ExecutionState = { ...INITIAL_STATE };
  private listeners: Set<Listener> = new Set();

  getState(): ExecutionState {
    return { ...this.state };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private notify(): void {
    const snapshot = this.getState();
    this.listeners.forEach((fn) => { try { fn(snapshot); } catch { /* swallow */ } });
  }

  private setState(partial: Partial<ExecutionState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  // ── Actions ──

  async submitPreview(request: TradePreviewRequest): Promise<void> {
    if (this.state.status === 'previewing' || this.state.status === 'executing') return;

    this.setState({
      status: 'previewing',
      preview: null,
      execution: null,
      error: null,
      errorType: null,
    });

    const result = await previewTrade(request);

    if (result.success && result.preview) {
      this.setState({
        status: 'idle',
        preview: result.preview,
        error: null,
        errorType: null,
      });
    } else {
      this.setState({
        status: 'error',
        error: result.error ?? 'Preview failed',
        errorType: result.errorType ?? 'unknown',
      });
    }
  }

  async confirmExecution(): Promise<void> {
    const previewId = this.state.preview?.previewId;
    if (!previewId) {
      this.setState({ status: 'error', error: 'No preview to execute', errorType: 'validation' });
      return;
    }
    if (this.state.status === 'executing') return;

    this.setState({ status: 'executing', error: null, errorType: null });

    const result = await adapterExecute(previewId);

    if (result.success && result.execution) {
      this.setState({
        status: 'success',
        execution: result.execution,
        error: null,
        errorType: null,
      });
    } else {
      this.setState({
        status: 'error',
        execution: result.execution ?? null,
        error: result.error ?? 'Execution failed',
        errorType: result.errorType ?? 'unknown',
      });
    }
  }

  reset(): void {
    this.setState({ ...INITIAL_STATE });
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  SINGLETON
// ═══════════════════════════════════════════════════════════════════════

export const executionStore = new ExecutionStoreClass();
